import {
  Button,
  Form,
  FormItem,
  Input,
  TextArea,
  useForm,
  Message,
  ColorPicker,
  InputNumber,
  Checkbox,
} from "@/frontend/components/common";
import { tournamentStatusService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type {
  CreateTournamentStatusRequestDTO,
  UpdateTournamentStatusRequestDTO,
  ApiResponse,
  TournamentStatusResponseDTO,
} from "@/shared/dto";
import { useEffect } from "react";

interface TournamentStatusFormValues {
  name: string;
  slug: string;
  description?: string;
  color?: string;
  isActive: boolean;
  sortOrder: number;
  isSystem: boolean;
  canDelete: boolean;
  canEdit: boolean;
}

interface TournamentStatusFormProps {
  tournamentStatusId?: string;
  initialValues?: Partial<TournamentStatusFormValues>;
}

export function TournamentStatusForm({
  tournamentStatusId,
  initialValues,
}: TournamentStatusFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!tournamentStatusId;

  useEffect(() => {
    if (initialValues && isEditMode) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, isEditMode, form]);

  const handleSubmit = async (values: TournamentStatusFormValues) => {
    try {
      let response: ApiResponse<TournamentStatusResponseDTO>;

      if (isEditMode && tournamentStatusId) {
        const updateData: UpdateTournamentStatusRequestDTO = {
          name: values.name,
          slug: values.slug,
          description: values.description || undefined,
          color: values.color || undefined,
          isActive: values.isActive,
          sortOrder: values.sortOrder,
          isSystem: values.isSystem,
          canDelete: values.canDelete,
          canEdit: values.canEdit,
        };
        response = await tournamentStatusService.update(
          tournamentStatusId,
          updateData,
        );
      } else {
        const createData: CreateTournamentStatusRequestDTO = {
          name: values.name,
          slug: values.slug,
          description: values.description || undefined,
          color: values.color || undefined,
          isActive: values.isActive,
          sortOrder: values.sortOrder,
          isSystem: values.isSystem,
          canDelete: values.canDelete,
          canEdit: values.canEdit,
        };
        response = await tournamentStatusService.create(createData);
      }

      if (response.success && response.data) {
        Message.success({
          content: isEditMode
            ? "Estado actualizado correctamente"
            : "Estado creado correctamente",
        });
        navigate("/tournament-statuses");
      } else {
        Message.error({
          content:
            response.error ||
            (isEditMode
              ? "Error al actualizar estado"
              : "Error al crear estado"),
        });
      }
    } catch (err) {
      console.error(err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : isEditMode
            ? "Error al actualizar estado"
            : "Error al crear estado";

      if (
        errorMessage.toLowerCase().includes("nombre") ||
        errorMessage.toLowerCase().includes("name")
      ) {
        form.setFields([
          {
            name: "name",
            errors: [errorMessage],
          },
        ]);
      } else {
        Message.error({ content: errorMessage });
      }
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className="w-full"
      initialValues={{
        slug: "",
        sortOrder: 0,
        isActive: true,
        isSystem: false,
        canDelete: true,
        canEdit: true,
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          name="name"
          label="Nombre del Estado"
          rules={[{ required: true, message: "El nombre es requerido" }]}
        >
          <Input placeholder="Ej: En curso, Finalizado" size="large" />
        </FormItem>

        <FormItem
          name="slug"
          label="Slug"
          rules={[{ required: true, message: "El slug es requerido" }]}
        >
          <Input placeholder="slug-ejemplo" size="large" />
        </FormItem>

        <FormItem
          name="sortOrder"
          label="Orden"
          rules={[{ required: true, message: "El orden es requerido" }]}
        >
          <InputNumber
            placeholder="0"
            size="large"
            min={0}
            className="w-full"
          />
        </FormItem>

        <FormItem
          name="isActive"
          label="Activo"
          valuePropName="checked"
        >
          <Checkbox>Estado activo</Checkbox>
        </FormItem>

        <FormItem
          name="isSystem"
          label="Sistema"
          valuePropName="checked"
        >
          <Checkbox>Marca como sistema</Checkbox>
        </FormItem>

        <FormItem
          name="canDelete"
          label="Permitir eliminar"
          valuePropName="checked"
        >
          <Checkbox>Puede eliminarse</Checkbox>
        </FormItem>

        <FormItem
          name="canEdit"
          label="Permitir editar"
          valuePropName="checked"
        >
          <Checkbox>Puede editarse</Checkbox>
        </FormItem>

        <FormItem
          name="description"
          label="Descripción"
          className="md:col-span-2"
        >
          <TextArea
            placeholder="Descripción (opcional)"
            rows={3}
            size="large"
          />
        </FormItem>

        <FormItem name="color" label="Color" className="md:col-span-2">
          <ColorPicker format="hex" />
        </FormItem>

      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button type="primary" htmlType="submit" size="large">
          {isEditMode ? "Actualizar Estado" : "Crear Estado"}
        </Button>
        <Button size="large" onClick={() => navigate("/tournament-statuses")}>
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
